/**
 * The two layers of the mosaic wall and how they compose.
 *
 * The campaign poster is the **backdrop**: it is painted in its own pane below
 * the tile pyramid, at full opacity, for the whole life of the page. The tile
 * layer fades in over it by zoom. So wherever a tile has arrived the visitor
 * sees the photo; wherever it has not, they see the campaign artwork — never
 * the map background `#05070d`.
 *
 * That inversion is the fix for two field defects that were the same shape:
 *
 * - 2026-10-24, the intro dive: the poster was a curtain *above* the tiles,
 *   gated on "one tile has ever loaded" (a fact about the session, not the
 *   view). The dive landed past the fade band and every slot still in flight
 *   showed black for 2–13 s.
 * - 2026-10-26, panning: the same curtain at an already-revealed zoom, so a
 *   drag into unloaded slots showed black columns while the JPEGs arrived.
 *
 * The old stacking existed because Leaflet paints tiles with
 * `mix-blend-mode: plus-lighter`, which washes out anything underneath. Put
 * the poster below and switch the tiles to normal blending: measured on
 * 2026-10-26 against the old arrangement, the settled wall differs by 3/255
 * mean per channel — the price of never showing a gap as black.
 *
 * Because the poster is always painted, no "has this zoom been revealed" state
 * is needed at all. The reveal set that shipped on 2026-10-25 is gone.
 */

export function clamp01(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return 0
  return x < 0 ? 0 : x > 1 ? 1 : x
}

/**
 * Tile-layer opacity: the zoom fade between the coarse poster and the photos.
 * 0 at the cover view, 1 once the photos are their own artwork.
 */
export function tileOpacity({tileMix, attached, seenTileLoad}) {
  if (!attached || !seenTileLoad) return 0
  return clamp01(tileMix)
}

/**
 * The poster is the backdrop and never fades out to nothing: at 0 a slot that
 * has not loaded would show the map background. It only hides behind opaque
 * tiles. `hidden` is for the case where the event has no poster at all.
 */
export function stillOpacity({hasPoster = true}) {
  return hasPoster ? 1 : 0
}

/** Wrap the world once the tile layer is the only thing on screen. */
export function wantsWrap({seenTileLoad, tileMix}) {
  return Boolean(seenTileLoad) && clamp01(tileMix) > 0.98
}

export default {
  clamp01,
  tileOpacity,
  stillOpacity,
  wantsWrap,
}
